import React, { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { Language } from "../modules/shared/model/LanguageInterfaces";
import { useAppDispatch, useAppSelector } from "../redux/store";
import { getAllLanguages } from "../redux/Actions/LanguageAction";

interface LanguageContextType {
  currentLanguage: Language | null;
  setCurrentLanguage: (language: Language | null) => void;
  activeLanguages: Language[];
  isLoading: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

const LANGUAGE_STORAGE_KEY = "kokobeo_selected_language";

export const LanguageProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const dispatch = useAppDispatch();
  const authLanguages = useAppSelector((state: any) => state.auth?.languages || []);
  
  const [currentLanguage, setCurrentLanguageState] = useState<Language | null>(null);
  const [activeLanguages, setActiveLanguages] = useState<Language[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedLanguages, setLoadedLanguages] = useState<Language[]>([]);

  // Load languages on mount
  useEffect(() => {
    const loadLanguages = async () => {
      try {
        setIsLoading(true);
        const result = await dispatch(getAllLanguages({
          page: 1,
          limit: 50,
          isShowLoader: false,
        })).unwrap();
        
        const languagesData = result?.data || result || [];
        setLoadedLanguages(languagesData);
      } catch (error) {
        console.error("Failed to load languages:", error);
      } finally {
        setIsLoading(false);
      }
    };

    loadLanguages();
  }, [dispatch]);

  // Filter active languages and restore saved language
  useEffect(() => {
    const languages = loadedLanguages.length > 0 ? loadedLanguages : authLanguages;
    
    if (!languages || languages.length === 0) {
      return;
    }

    const active = languages;
    setActiveLanguages(active);

    const savedLanguageId = localStorage.getItem(LANGUAGE_STORAGE_KEY);
    
    if (savedLanguageId && savedLanguageId !== "null" && savedLanguageId !== "undefined") {
      const savedLang = active.find((lang: Language) => 
        lang.id === savedLanguageId || 
        String(lang.id) === String(savedLanguageId) ||
        String(lang.id).trim() === String(savedLanguageId).trim()
      );
      
      if (savedLang) {
        setCurrentLanguageState(savedLang);
        return;
      }
    }

    // Default to English or first active language
    const defaultLang = active.find((lang: Language) => 
      lang.name?.toLowerCase() === "english" || lang.isDefault
    ) || active[0];
    
    if (defaultLang) {
      setCurrentLanguageState(defaultLang);
      if (!savedLanguageId || savedLanguageId === "null" || savedLanguageId === "undefined") {
        localStorage.setItem(LANGUAGE_STORAGE_KEY, defaultLang.id);
      }
    }
  }, [loadedLanguages, authLanguages]);

  const setCurrentLanguage = (language: Language | null) => {
    if (!language) {
      // Clear language selection
      setCurrentLanguageState(null);
      localStorage.removeItem(LANGUAGE_STORAGE_KEY);
      return;
    }
    
    setCurrentLanguageState(language);
    localStorage.setItem(LANGUAGE_STORAGE_KEY, language.id);
  };

  return (
    <LanguageContext.Provider
      value={{
        currentLanguage,
        setCurrentLanguage,
        activeLanguages,
        isLoading,
      }}
    >
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguageContext = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error("useLanguageContext must be used within a LanguageProvider");
  }
  return context;
};

